import React, { useState, useEffect } from 'react';
import { inventoryAPI, menuAPI } from '../services/api';
import CSVUpload from './CSVUpload';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Package, Edit, AlertTriangle, Upload } from 'lucide-react';
import { toast } from 'sonner';

export default function InventoryManagement() {
  const [inventory, setInventory] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restockingItem, setRestockingItem] = useState(null);
  const [restockQuantity, setRestockQuantity] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [activeTab, setActiveTab] = useState('inventory'); // 'inventory' or 'upload'

  useEffect(() => {
    fetchInventory();
    fetchLowStock();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await inventoryAPI.getAll();
      if (response.data.success) {
        setInventory(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch inventory', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLowStock = async () => {
    try {
      const response = await inventoryAPI.getLowStock();
      if (response.data.success) {
        setLowStock(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch low stock items', err);
    }
  };

  const handleRestock = async (itemId) => {
    if (!restockQuantity || parseInt(restockQuantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    try {
      await inventoryAPI.restock(itemId, parseInt(restockQuantity), 'Manual restock');
      toast.success('Item restocked successfully');
      setRestockingItem(null);
      setRestockQuantity('');
      fetchInventory();
      fetchLowStock();
    } catch (err) {
      toast.error('Failed to restock item');
    }
  };

  const startEdit = (item) => {
    setEditingItem(item._id);
    setEditFormData({
      name: item.name,
      description: item.description,
      category: item.category,
      price: item.price.toString(),
      stock: item.stock.toString(),
    });
  };

  const handleEditSubmit = async () => {
    try {
      await menuAPI.update(editingItem, {
        ...editFormData,
        price: parseFloat(editFormData.price),
        stock: parseInt(editFormData.stock),
      });
      toast.success('Item updated successfully');
      setEditingItem(null);
      setEditFormData({});
      fetchInventory();
      fetchLowStock();
    } catch (err) {
      toast.error('Failed to update item');
      console.error(err);
    }
  };

  const getStockBadgeVariant = (stock) => {
    if (stock < 5) return 'destructive';
    if (stock < 20) return 'default'; // orange
    return 'secondary'; // green
  };

  if (loading && activeTab === 'inventory') return <div className="p-4">Loading...</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Package className="h-8 w-8 text-orange-600" />
            Inventory Management
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('inventory')}
            variant={activeTab === 'inventory' ? 'default' : 'outline'}
            className="gap-2"
          >
            <Package className="h-4 w-4" />
            Inventory
          </Button>
          <Button
            onClick={() => setActiveTab('upload')}
            variant={activeTab === 'upload' ? 'default' : 'outline'}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            Bulk Upload
          </Button>
        </div>

        {activeTab === 'inventory' && (
          <>
            {/* Low Stock Alert */}
            {lowStock.length > 0 && (
              <Card className="mb-6 border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-red-800 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Low Stock Items ({lowStock.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {lowStock.map((item) => (
                      <div key={item._id} className="bg-white p-4 rounded-lg border border-red-200">
                        <p className="font-semibold text-gray-800 mb-2">{item.name}</p>
                        <Badge variant="destructive" className="font-bold">
                          Stock: {item.stock}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Inventory Table */}
            <Card>
              <CardHeader>
                <CardTitle>All Items ({inventory.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Item</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
                        <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">Price</th>
                        <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">Stock</th>
                        <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {inventory.map((item) => (
                        <tr key={item._id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-800">{item.name}</div>
                            {item.description && (
                              <div className="text-sm text-gray-500">{item.description}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-gray-600">{item.category}</td>
                          <td className="px-6 py-4 text-right font-semibold text-gray-800">₹{item.price.toFixed(2)}</td>
                          <td className="px-6 py-4 text-center">
                            <Badge variant={getStockBadgeVariant(item.stock)} className="font-bold">
                              {item.stock}
                            </Badge>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2 justify-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => startEdit(item)}
                                className="gap-1"
                              >
                                <Edit className="h-3 w-3" />
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => setRestockingItem(item._id)}
                                className="gap-1"
                              >
                                <Package className="h-3 w-3" />
                                Restock
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-200">
                  {inventory.map((item) => (
                    <div key={item._id} className="p-4 hover:bg-gray-50 transition">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800 text-base mb-1">{item.name}</h3>
                          <p className="text-sm text-gray-600">{item.category}</p>
                        </div>
                        <Badge variant={getStockBadgeVariant(item.stock)} className="font-bold">
                          {item.stock}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-lg font-bold text-gray-800">₹{item.price.toFixed(2)}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEdit(item)}
                          className="flex-1 gap-1"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setRestockingItem(item._id)}
                          className="flex-1 gap-1"
                        >
                          <Package className="h-4 w-4" />
                          Restock
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Restock Dialog */}
            <Dialog open={!!restockingItem} onOpenChange={(open) => {
              if (!open) {
                setRestockingItem(null);
                setRestockQuantity('');
              }
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-orange-600" />
                    Restock Item
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="restock-quantity">Quantity to Add</Label>
                    <Input
                      id="restock-quantity"
                      type="number"
                      value={restockQuantity}
                      onChange={(e) => setRestockQuantity(e.target.value)}
                      placeholder="Enter quantity"
                      min="1"
                      className="text-lg"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setRestockingItem(null);
                      setRestockQuantity('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={() => handleRestock(restockingItem)}>
                    Confirm Restock
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={!!editingItem} onOpenChange={(open) => {
              if (!open) {
                setEditingItem(null);
                setEditFormData({});
              }
            }}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Edit className="h-5 w-5 text-orange-600" />
                    Edit Item
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="item-name">Item Name</Label>
                    <Input
                      id="item-name"
                      type="text"
                      placeholder="Item Name"
                      value={editFormData.name || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="item-description">Description</Label>
                    <Input
                      id="item-description"
                      type="text"
                      placeholder="Description"
                      value={editFormData.description || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="item-category">Category</Label>
                    <select
                      id="item-category"
                      value={editFormData.category || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option>Main Course</option>
                      <option>Appetizer</option>
                      <option>Dessert</option>
                      <option>Beverage</option>
                      <option>Bread</option>
                      <option>Breakfast</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="item-price">Price (₹)</Label>
                      <Input
                        id="item-price"
                        type="number"
                        placeholder="Price"
                        value={editFormData.price || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, price: e.target.value })}
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="item-stock">Stock</Label>
                      <Input
                        id="item-stock"
                        type="number"
                        placeholder="Stock"
                        value={editFormData.stock || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, stock: e.target.value })}
                        min="0"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingItem(null);
                      setEditFormData({});
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleEditSubmit}>
                    Update Item
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}

        {activeTab === 'upload' && <CSVUpload />}
      </div>
    </div>
  );
}
